import * as React from 'react';
import { supabase, supabaseAdmin } from '../services/supabaseClient';
import { User, Session, AuthChangeEvent, UserIdentity } from '@supabase/supabase-js';
import {
  saveAuthReturnRoute,
  toPathString,
  isSafeInternalPath,
} from '../services/authReturnService';

// Define the CustomUser type with all required User properties
interface CustomUser extends Omit<User, 'phone' | 'identities' | 'confirmed_at' | 'email_confirmed_at' | 'phone_confirmed_at' | 'last_sign_in_at'> {
  name: string | null;
  role: string;
  phone: string | null;
  confirmed_at: string | null;
  email_confirmed_at: string | null;
  phone_confirmed_at: string | null;
  last_sign_in_at: string | null;
  achievements?: {
    [key: string]: {
      unlocked: boolean;
      unlockedAt?: string;
      progress: number;
    };
  };
  app_metadata: {
    provider?: string;
    providers?: string[];
  };
  user_metadata: {
    name?: string;
    role?: string;
  };
  identities: Array<{
    id: string;
    user_id: string;
    identity_data: {
      [key: string]: any;
      email?: string;
      sub?: string;
    };
    provider: string;
    created_at: string;
    updated_at: string;
    last_sign_in_at: string;
  }>;
}

interface RegistrationResult {
  success: boolean;
  message: string;
  errorType?: 'user_exists' | 'weak_password' | 'invalid_email' | 'rate_limited' | 'signup_disabled' | 'network' | 'other';
  retryAfterSeconds?: number;
}

interface RegistrationAvailabilityResult {
  emailTaken: boolean;
  nameTaken: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: CustomUser | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<RegistrationResult>;
  checkRegistrationAvailability: (name: string, email: string) => Promise<RegistrationAvailabilityResult>;
  signInWithProvider: (provider: 'google' | 'github' | 'facebook' | 'linkedin_oidc' | 'discord' | 'twitter') => Promise<void>;
  resetPassword: (email: string) => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
  demoLogin: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

const normalizeUrl = (value?: string | null) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return null;
  try {
    return new URL(trimmed).toString().replace(/\/$/, '');
  } catch {
    return null;
  }
};

const getRuntimeOrigin = () => {
  if (typeof window === 'undefined') return null;
  return normalizeUrl(window.location.origin);
};

const isLocalOrigin = (origin: string | null) => {
  if (!origin) return false;
  try {
    const url = new URL(origin);
    return url.hostname === 'localhost' || url.hostname === '127.0.0.1';
  } catch {
    return false;
  }
};

const getAuthBaseUrl = () => {
  const runtimeOrigin = getRuntimeOrigin();
  const configuredAppUrl = normalizeUrl(import.meta.env.VITE_APP_URL);
  const configuredLocalAppUrl = normalizeUrl(import.meta.env.VITE_LOCAL_APP_URL);

  if (isLocalOrigin(runtimeOrigin)) return runtimeOrigin;
  if (configuredAppUrl) return configuredAppUrl;
  if (runtimeOrigin) return runtimeOrigin;
  return configuredAppUrl ?? configuredLocalAppUrl ?? 'http://localhost:3000';
};

const buildAuthRedirectUrl = (path: string) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${getAuthBaseUrl()}${normalizedPath}`;
};

const captureCurrentAsReturnRoute = () => {
  if (typeof window === 'undefined') return null;
  try {
    return saveAuthReturnRoute(window.location as unknown as Location);
  } catch {
    return null;
  }
};

const extractRetryAfterSeconds = (message: string) => {
  const m = message.match(/(\d+)\s*(seconds?|secs?|minutes?|mins?)/i);
  if (!m) return null;
  const value = Number(m[1]);
  if (!Number.isFinite(value) || value <= 0) return null;
  const unit = m[2].toLowerCase();
  if (unit.startsWith('min')) return value * 60;
  return value;
};

const isNetworkError = (err: any) => {
  const msg = String(err?.message ?? '').toLowerCase();
  return msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('network request failed');
};

const formatRegistrationError = (err: any): Pick<RegistrationResult, 'message' | 'errorType' | 'retryAfterSeconds'> => {
  const raw = String(err?.message ?? 'Registration failed. Please try again.');
  const lower = raw.toLowerCase();
  const status = Number(err?.status ?? 0);

  if (status === 429 || lower.includes('rate limit') || lower.includes('too many requests')) {
    const retryAfterSeconds = extractRetryAfterSeconds(raw) ?? 60;
    return {
      errorType: 'rate_limited',
      retryAfterSeconds,
      message: `Too many sign-up attempts. Please wait ${retryAfterSeconds} seconds and try again.`,
    };
  }

  if (lower.includes('signups not allowed') || lower.includes('signup is disabled') || lower.includes('sign up is disabled')) {
    return {
      errorType: 'signup_disabled',
      message: 'Sign-up is temporarily disabled. Please contact support or try again later.',
    };
  }

  if (lower.includes('user already registered') || lower.includes('already registered') || lower.includes('already exists')) {
    return {
      errorType: 'user_exists',
      message: 'This email may already have an account. Try logging in or reset your password.',
    };
  }

  if (lower.includes('invalid email') || lower.includes('email address is invalid')) {
    return {
      errorType: 'invalid_email',
      message: 'Please enter a valid email address.',
    };
  }

  if (lower.includes('password should be at least') || lower.includes('weak password') || lower.includes('weak_password')) {
    return {
      errorType: 'weak_password',
      message: 'Please choose a stronger password (preferably a long passphrase).',
    };
  }

  if (isNetworkError(err)) {
    return {
      errorType: 'network',
      message: 'Network error. Please check your internet connection and try again.',
    };
  }

  return { errorType: 'other', message: raw };
};

const formatResetPasswordError = (err: any) => {
  const raw = String(err?.message ?? 'Failed to send password reset instructions. Please try again.');
  const lower = raw.toLowerCase();
  const status = Number(err?.status ?? 0);

  if (status === 429 || lower.includes('rate limit') || lower.includes('too many requests')) {
    const retryAfterSeconds = extractRetryAfterSeconds(raw) ?? 60;
    return { message: `Too many requests. Please wait ${retryAfterSeconds} seconds and try again.`, retryAfterSeconds };
  }

  if (isNetworkError(err)) {
    return { message: 'Network error. Please check your internet connection and try again.' };
  }

  return { message: raw };
};

/**
 * Authentication Provider component that manages user authentication state
 * Provides login, register, and logout functionality to the application
 * @param children - React components that will have access to the auth context
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(() => {
    const stored = localStorage.getItem('isAuthenticated');
    return stored === 'true';
  });

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [user, setUser] = React.useState<CustomUser | null>(() => {
    try {
      const stored = localStorage.getItem('user');
      if (!stored) return null;
      return JSON.parse(stored) as CustomUser;
    } catch {
      try {
        localStorage.removeItem('user');
        localStorage.removeItem('isAuthenticated');
      } catch {
        return null;
      }
      return null;
    }
  });

  const clearError = () => setError(null);
  const hasInitializedRef = React.useRef(false);

  /**
   * Persists authentication state and user data to localStorage
   * whenever they change
   */
  React.useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const withTimeout = async <T,>(promise: PromiseLike<T>, ms: number): Promise<T> => {
      let timeoutId: number | undefined;
      const timeout = new Promise<T>((_, reject) => {
        timeoutId = window.setTimeout(() => reject(new Error('AUTH_TIMEOUT')), ms);
      });
      try {
        return await Promise.race([Promise.resolve(promise as any), timeout]);
      } finally {
        if (timeoutId) window.clearTimeout(timeoutId);
      }
    };

    const isAuthTimeout = (e: unknown) => e instanceof Error && e.message === 'AUTH_TIMEOUT';

    const clearAuthStorage = () => {
      try {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('user');
      } catch {
      }
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (!key) continue;
          if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
            localStorage.removeItem(key);
          }
        }
      } catch {
      }
    };

    // Check for existing session
    const checkSession = async () => {
      try {
        const { data: { session } } = await withTimeout(supabase.auth.getSession(), 20000);

        if (!session?.user) {
          setUser(null);
          setIsAuthenticated(false);
          return;
        }

        const baseUser = session.user;
        setIsAuthenticated(true);

        let profile: any = null;
        try {
          await withTimeout(ensureUserProfile(baseUser), 20000);
        } catch (e) {
          if (!isAuthTimeout(e)) console.error('Error ensuring user profile:', e);
        }

        try {
          const profileResult = await withTimeout(supabase
            .from('profiles')
            .select('*')
            .eq('id', baseUser.id)
            .maybeSingle(), 20000);
          profile = (profileResult as any)?.data ?? null;
        } catch (e) {
          if (!isAuthTimeout(e)) console.error('Error loading profile:', e);
        }

        const customUser: CustomUser = {
          ...baseUser,
          name: profile?.name || baseUser.user_metadata?.name || (baseUser.user_metadata as any)?.full_name || null,
          role: profile?.role || (baseUser.user_metadata as any)?.role || 'user',
          phone: baseUser.phone || null,
          confirmed_at: baseUser.confirmed_at || null,
          email_confirmed_at: baseUser.email_confirmed_at || null,
          phone_confirmed_at: baseUser.phone_confirmed_at || null,
          last_sign_in_at: baseUser.last_sign_in_at || null,
          app_metadata: baseUser.app_metadata || {},
          user_metadata: baseUser.user_metadata || {},
          identities: (baseUser.identities || []).map(identity => ({
            id: identity.id,
            user_id: identity.user_id,
            identity_data: identity.identity_data || { email: undefined, sub: undefined },
            provider: identity.provider,
            created_at: identity.created_at || baseUser.created_at,
            updated_at: identity.updated_at || identity.created_at || baseUser.created_at,
            last_sign_in_at: identity.last_sign_in_at || baseUser.last_sign_in_at || baseUser.created_at
          }))
        };
        setUser(customUser);
      } catch (error) {
        if (isAuthTimeout(error)) {
          setIsAuthenticated((prev) => prev);
          setUser((prev) => prev);
        } else {
          console.error('Error checking session:', error);
          clearAuthStorage();
          setUser(null);
          setIsAuthenticated(false);
        }
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event: AuthChangeEvent, session: Session | null) => {
        try {
          if (!session?.user) {
            setUser(null);
            setIsAuthenticated(false);
            return;
          }

          try {
            await withTimeout(ensureUserProfile(session.user), 20000);
          } catch (e) {
            if (!isAuthTimeout(e)) console.error('Error ensuring user profile:', e);
          }

          let profile: any = null;
          try {
            const profileResult = await withTimeout(supabase
              .from('profiles')
              .select('*')
              .eq('id', session.user.id)
              .maybeSingle(), 20000);
            profile = (profileResult as any)?.data ?? null;
          } catch (e) {
            if (!isAuthTimeout(e)) console.error('Error loading profile:', e);
          }

          const customUser: CustomUser = {
            ...session.user,
            name: profile?.name || session.user.user_metadata?.name || (session.user.user_metadata as any)?.full_name || null,
            role: profile?.role || (session.user.user_metadata as any)?.role || 'user',
            phone: session.user.phone || null,
            confirmed_at: session.user.confirmed_at || null,
            email_confirmed_at: session.user.email_confirmed_at || null,
            phone_confirmed_at: session.user.phone_confirmed_at || null,
            last_sign_in_at: session.user.last_sign_in_at || null,
            app_metadata: session.user.app_metadata || {},
            user_metadata: session.user.user_metadata || {},
            identities: (session.user.identities || []).map(identity => ({
              id: identity.id,
              user_id: identity.user_id,
              identity_data: identity.identity_data || { email: undefined, sub: undefined },
              provider: identity.provider,
              created_at: identity.created_at || session.user.created_at,
              updated_at: identity.updated_at || identity.created_at || session.user.created_at,
              last_sign_in_at: identity.last_sign_in_at || session.user.last_sign_in_at || session.user.created_at
            }))
          };
          setUser(customUser);
          setIsAuthenticated(true);
        } catch (e) {
          console.error('Auth state change handler error:', e);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    try {
      localStorage.setItem('isAuthenticated', isAuthenticated.toString());
    } catch {
    }
    try {
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      } else {
        localStorage.removeItem('user');
      }
    } catch {
    }
  }, [isAuthenticated, user]);

  // Helper to ensure a user profile exists in the profiles table
  async function ensureUserProfile(user: any) {
    if (!user) return;
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session?.user) return;

    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (!profile) {
      const { error: insertError } = await supabase.from('profiles').insert([
        {
          id: user.id,
          email: user.email,
          name: user.user_metadata?.name || (user.user_metadata as any)?.full_name || '',
          role: user.user_metadata?.role || 'farmer',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ]);
      if (insertError) throw insertError;
    }
  }

  /**
   * Authenticates a user with their email and password
   * @param email - The user's email address
   * @param password - The user's password
   * @returns Promise that resolves when login is complete
   */
  const login = async (email: string, password: string) => {
    setError(null);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        if (error.message.includes('Email not confirmed')) {
          const msg = 'Please verify your email before logging in. Check your inbox for the verification link.';
          setError(msg);
          throw new Error(msg);
        }
        setError(error.message);
        throw error;
      }
      if (data.user) {
        await ensureUserProfile(data.user);
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.user.id)
          .maybeSingle();
        const customUser: CustomUser = {
          ...data.user,
          name: profile?.name || null,
          role: profile?.role || 'user',
          phone: data.user.phone || null,
          confirmed_at: data.user.confirmed_at || null,
          email_confirmed_at: data.user.email_confirmed_at || null,
          phone_confirmed_at: data.user.phone_confirmed_at || null,
          last_sign_in_at: data.user.last_sign_in_at || null,
          app_metadata: data.user.app_metadata || {},
          user_metadata: data.user.user_metadata || {},
          identities: (data.user.identities || []).map(identity => ({
            id: identity.id,
            user_id: identity.user_id,
            identity_data: identity.identity_data || { email: undefined, sub: undefined },
            provider: identity.provider,
            created_at: identity.created_at || data.user.created_at,
            updated_at: identity.updated_at || identity.created_at || data.user.created_at,
            last_sign_in_at: identity.last_sign_in_at || data.user.last_sign_in_at || data.user.created_at
          }))
        };
        setIsAuthenticated(true);
        setUser(customUser);
        // Navigation handled by calling component
      }
    } catch (error: any) {
      console.error('Login failed:', error);
      if (!error.message) {
        setError('Login failed');
      }
      throw error;
    }
  };

  /**
   * Authenticates a user with a third-party provider (Google, GitHub, etc.)
   * @param provider - The OAuth provider to use
   */
  const signInWithProvider = async (provider: 'google' | 'github' | 'facebook' | 'linkedin_oidc' | 'discord' | 'twitter') => {
    setError(null);
    try {
      captureCurrentAsReturnRoute();
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: buildAuthRedirectUrl('/dashboard'),
        },
      });
      if (error) throw error;
    } catch (error: any) {
      console.error(`${provider} login failed:`, error);
      setError(error.message || `${provider} login failed`);
      throw error;
    }
  };

  /**
   * Registers a new user with the provided information
   * @param name - The user's name
   * @param email - The user's email address
   * @param password - The user's chosen password
   * @returns Promise that resolves when registration is complete
   */
  const register = async (name: string, email: string, password: string): Promise<RegistrationResult> => {
    setError(null);
    try {
      const normalizedName = String(name ?? '').trim();
      const normalizedEmail = String(email ?? '').trim().toLowerCase();

      // First, sign up the user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
          options: {
            data: {
              name: normalizedName,
            },
            // After registration send the confirmation email to the verification
            // page. That page will confirm the token and close the tab so users
            // don't land on a blank Supabase page.
            emailRedirectTo: buildAuthRedirectUrl('/verify-email'),
          },
      });

      if (authError) {
        const formatted = formatRegistrationError(authError);
        setError(formatted.message);
        return { success: false, ...formatted };
      }

      if (authData.session?.user) {
        try {
          await ensureUserProfile(authData.session.user);
        } catch (profileError) {
          console.warn('Profile creation after registration failed:', profileError);
        }
      }

      if (authData.user || authData.session?.user || !authError) {
        return {
          success: true,
          message: 'Registration successful! Please check your email to verify your account.',
        };
      }

      const msg =
        'Sign-up did not complete. Please try again. If this keeps happening, sign-up may be disabled or your network may be blocking the request.';
      setError(msg);
      return { success: false, message: msg, errorType: 'other' };
    } catch (error: any) {
      console.error('Registration failed:', error);
      const formatted = formatRegistrationError(error);
      setError(formatted.message);
      return { success: false, ...formatted };
    }
  };

  const checkRegistrationAvailability = async (name: string, email: string): Promise<RegistrationAvailabilityResult> => {
    const normalizedName = String(name ?? '').trim();
    const normalizedEmail = String(email ?? '').trim().toLowerCase();

    if (!normalizedName && !normalizedEmail) {
      return { emailTaken: false, nameTaken: false };
    }

    try {
      const { data, error } = await supabase.rpc('check_registration_availability', {
        p_name: normalizedName || null,
        p_email: normalizedEmail || null,
      });

      if (error) throw error;

      const row = Array.isArray(data) ? data[0] : data;
      return {
        emailTaken: Boolean(row?.email_taken),
        nameTaken: Boolean(row?.name_taken),
      };
    } catch (availabilityError) {
      console.warn('Registration availability check failed:', availabilityError);
      return { emailTaken: false, nameTaken: false };
    }
  };

  const resetPassword = async (email: string): Promise<{ success: boolean; message: string }> => {
    setError(null);
    try {
      const normalizedEmail = String(email ?? '').trim().toLowerCase();
      const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
        redirectTo: buildAuthRedirectUrl('/reset-password'),
      });

      if (error) throw error;

      return {
        success: true,
        message: 'Password reset instructions have been sent to your email.',
      };
    } catch (error: any) {
      console.error('Password reset failed:', error);
      const formatted = formatResetPasswordError(error);
      setError(formatted.message);
      return {
        success: false,
        message: formatted.message,
      };
    }
  };

  /**
   * Logs out the current user
   * Clears authentication state and removes user data from storage
   */
  const logout = async () => {
    setError(null);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('user');
      await supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          console.log('Session still exists, forcing clear');
          document.cookie.split(';').forEach(function (c) {
            document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
          });
        }
      });
      // Navigate to home handled by calling component
    } catch (error: any) {
      console.error('Logout failed:', error);
      setError(error.message || 'Logout failed');
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('user');
      // Navigate to home handled by calling component
    }
  };


  const logoutRef = React.useRef(logout);
  React.useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  React.useEffect(() => {
    if (!isAuthenticated) return;

    const idleTimeoutMs = 60 * 60 * 1000;
    const minEventIntervalMs = 15000;
    let timeoutId: number | null = null;
    let lastActivityAt = Date.now();

    const clear = () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      timeoutId = null;
    };

    const schedule = () => {
      clear();
      timeoutId = window.setTimeout(() => {
        logoutRef.current().catch(() => {});
      }, idleTimeoutMs);
    };

    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityAt < minEventIntervalMs) return;
      lastActivityAt = now;
      schedule();
    };

    schedule();

    window.addEventListener('mousemove', onActivity, { passive: true });
    window.addEventListener('mousedown', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity, { passive: true });
    window.addEventListener('scroll', onActivity, { passive: true });
    window.addEventListener('touchstart', onActivity, { passive: true });

    return () => {
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('mousedown', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('scroll', onActivity);
      window.removeEventListener('touchstart', onActivity);
      clear();
    };
  }, [isAuthenticated]);

  const demoLogin = async () => {
    const demoEmail = import.meta.env.VITE_DEMO_EMAIL || 'free@demo.com';
    const demoPassword = import.meta.env.VITE_DEMO_PASSWORD || 'password123';
    await login(demoEmail, demoPassword);
  };

  const value = React.useMemo(() => ({
    isAuthenticated,
    user,
    loading,
    error,
    clearError,
    login,
    register,
    checkRegistrationAvailability,
    signInWithProvider,
    resetPassword,
    logout,
    demoLogin
  }), [isAuthenticated, user, loading, error]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to access the authentication context
 * @returns Authentication context with user state and auth functions
 * @throws Error if used outside of AuthProvider
 */
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
